import React from 'react';

import { mergeRefs } from '../components/react-component-lib/utils';
import { FmlLifeCycleContext } from '../contexts/FmlLifeCycleContext';
import type { RouteInfo } from '../models';

import { StackContext } from './StackContext';

interface PageManagerProps {
  className?: string;
  forwardedRef?: React.ForwardedRef<HTMLDivElement>;
  routeInfo?: RouteInfo;
}

export class PageManager extends React.PureComponent<PageManagerProps> {
  fmlLifeCycleContext!: React.ContextType<typeof FmlLifeCycleContext>;
  context!: React.ContextType<typeof StackContext>;
  fmlPageElementRef: React.RefObject<HTMLDivElement>;
  stableMergedRefs: React.RefCallback<HTMLDivElement>;

  constructor(props: PageManagerProps) {
    super(props);
    this.fmlPageElementRef = React.createRef();
    // React refs must be stable (not created inline).
    this.stableMergedRefs = mergeRefs(this.fmlPageElementRef, this.props.forwardedRef);

    /**
     * This binds the scope of the following methods to the class scope.
     * The `.bind` method returns a new function, so we need to assign it
     * in the constructor rather than when adding or removing the listeners
     * to avoid creating a new function.
     */
    this.fmlViewWillEnterHandler = this.fmlViewWillEnterHandler.bind(this);
    this.fmlViewDidEnterHandler = this.fmlViewDidEnterHandler.bind(this);
    this.fmlViewWillLeaveHandler = this.fmlViewWillLeaveHandler.bind(this);
    this.fmlViewDidLeaveHandler = this.fmlViewDidLeaveHandler.bind(this);
  }

  componentDidMount() {
    if (this.fmlPageElementRef.current) {
      if (this.context.isInOutlet()) {
        this.fmlPageElementRef.current.classList.add('fml-page-invisible');
      }
      this.context.registerFmlPage(this.fmlPageElementRef.current, this.props.routeInfo!);
      this.fmlPageElementRef.current.addEventListener('fmlViewWillEnter', this.fmlViewWillEnterHandler);
      this.fmlPageElementRef.current.addEventListener('fmlViewDidEnter', this.fmlViewDidEnterHandler);
      this.fmlPageElementRef.current.addEventListener('fmlViewWillLeave', this.fmlViewWillLeaveHandler);
      this.fmlPageElementRef.current.addEventListener('fmlViewDidLeave', this.fmlViewDidLeaveHandler);
    }
  }

  componentWillUnmount() {
    if (this.fmlPageElementRef.current) {
      this.fmlPageElementRef.current.removeEventListener('fmlViewWillEnter', this.fmlViewWillEnterHandler);
      this.fmlPageElementRef.current.removeEventListener('fmlViewDidEnter', this.fmlViewDidEnterHandler);
      this.fmlPageElementRef.current.removeEventListener('fmlViewWillLeave', this.fmlViewWillLeaveHandler);
      /**
       * We deliberately do not remove the `fmlViewDidLeave` listener.
       * The registered callback is used to unmount and remove the page.
       * Removing the event listener prevents the callback from being called.
       * The browser will automatically remove the event listener when the
       * page element is removed from the DOM and garbage collected.
       */
    }
  }

  fmlViewWillEnterHandler() {
    this.fmlLifeCycleContext.fmlViewWillEnter();
  }

  fmlViewDidEnterHandler() {
    this.fmlLifeCycleContext.fmlViewDidEnter();
  }

  fmlViewWillLeaveHandler() {
    this.fmlLifeCycleContext.fmlViewWillLeave();
  }

  fmlViewDidLeaveHandler() {
    this.fmlLifeCycleContext.fmlViewDidLeave();
  }

  render() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { className, children, routeInfo, forwardedRef, ...props } = this.props;

    return (
      <FmlLifeCycleContext.Consumer>
        {(context) => {
          this.fmlLifeCycleContext = context;
          return (
            <div className={className ? `${className} fml-page` : `fml-page`} ref={this.stableMergedRefs} {...props}>
              {children}
            </div>
          );
        }}
      </FmlLifeCycleContext.Consumer>
    );
  }

  static get contextType() {
    return StackContext;
  }
}
export default PageManager;
